﻿// Mobilní drawer navigace — drill-down zprava dle handoveru Claude Design
// (design_handoff_mobilni_menu/README.md, koncept 2a v „Mobilní menu.dc.html";
// nahrazuje screens/m01-header.md §„Drawer navigace" i HeaderMobil.dc.html).
// Aktivní ≤ $bp-xl (mobil i tablet).
//
// Panel JE nativní `nav#navigation` — nestavíme kopii menu, jen ho dekorujeme
// (Shoptet `class`, `data-testid` i pořadí uzlů zůstávají). Úrovně jedou přes
// vnořené `ul.menu-level-2/3`, které CSS pozicuje jako sourozenecké panely; stav
// drží třídy `li.is-open` (styly a mechanika = naše, viz _drawer.scss).
//
// Otevření NEDELEGUJEME na default JS (`body.navigation-window-visible`): offline
// fixtures ani Bender bez runtime by drawer neotevřely a stav by se rozjel se
// zavíráním tapem mimo. Klik na hamburger proto přebíráme v capture fázi
// (stejný vzor jako cartCounter.js) a stav držíme vlastní třídou.
//
// Rozhodnutí Tomáše 2026-08-31 (nadstavba nad handover, viz CLAUDE.md):
//   - klik na NÁZEV kategorie s podúrovní OTEVÍRÁ podúroveň (drill-down přes
//     celý řádek, jak kreslí handover). ⚠️ Dřívější verze z téhož dne (název
//     naviguje, drill jen chevronem) byla dezinterpretace zadání a Tomáš ji
//     večer 31. 8. vrátil; chevron tlačítko zůstává jako viditelná šipka i cíl,
//   - HLAVIČKA panelu (ikona + název + počet) NAVIGUJE na výpis kategorie —
//     totéž co „Zobrazit vše" vedle ní (upřesnění po nasazení 31. 8.),
//   - krok ZPĚT dělá výhradně zpět-řádek, klikatelný CELÝ (ne jen text „← zpět"),
//   - drawer se otevírá V AKTUÁLNÍ kategorii (nativní `li.active` v menu,
//     fallback shoda canonical URL), ne vždy na hlavním menu,
//   - vyhledávání kategorií a sekce Nakupování / Jipos.cz / karta podpory jsou
//     v KAŽDÉM panelu, ne jen na úrovni 1,
//   - Stav objednávky = tmavé tlačítko hned pod kategoriemi, nad sekcí
//     Nakupování (Tomáš 2026-10-09, mock V1), ne řádek sekce.
//
// Vyhledávání je čistě klientské nad TÝMŽ stromem, který plní menu (readMenu) —
// case-insensitive, bez diakritiky (NFD normalizace), max 14 výsledků, žádné
// API navíc. Každý panel má vlastní pole; stav `is-searching` žije na panelu.
//
// Pojistka: dokud modul nedoběhne, chybí `html.bs-nav-ready` a CSS nechá nativní
// (nestylovaný, ale funkční) strom menu — stejný princip jako `bs-header-ready`.

import { icon, iconHtml, el } from './icon.js';
import { CAT_ICONS, pathOf, readMenu } from './menuModel.js';
import { ACCOUNT, ACCOUNT_SIGNED, CONTACT, WISHLIST, isSignedIn, isSupportOnline, reachContact } from './headerTools.js';
import { asChatTrigger } from './supportChat.js';
import { FAQ, HOURS } from '../locale/cs/common.js';
import L from '../locale/cs/mobileNav.js';
import { fillAvatar } from './teamPhoto.js';
import { page, withPages } from './pages.js';
import { flag, sisterLink } from './countrySwitch.js';
import SHOP from '../../config/shop.js';
import SISTER from '../../config/sister.js';

const LABEL_CLOSE = L.closeMenu;
const LABEL_ROOT = L.rootMenu;
const LABEL_ALL = L.showAll;
const LABEL_NAV = L.nav;
const LABEL_SUPPORT = L.support;
const LABEL_DRILL = L.showSubcats;

const SEARCH_PLACEHOLDER = L.search.placeholder;
const SEARCH_CLEAR = L.search.clear;
const SEARCH_EMPTY = L.search.empty;
const SEARCH_MAX = 14;

const SALE_PATH = page('sale');
const OUTLET_PATH = page('clearance');

// Sekce „Nakupování". Oblíbené sdílí přepínač s hlavičkou — dokud wishlist
// v adminu neběží (issue #35), řádek se nekreslí (handover ho má).
// První řádek se odvíjí od přihlášení (#223) — proto funkce, ne konstanta.
// Reklamace/vrácení vedou PŘÍMO na stránky s Retino formulářem (zadání Tomáše
// 2026-08-31), ne na reklamační řád — na podmínky odkazují až samy ty stránky.
const SHOP_LINKS = withPages([
  { href: WISHLIST.href, label: WISHLIST.label, icon: 'heart', enabled: WISHLIST.enabled },
  { href: page('claim'), label: L.shop.claim, icon: 'shield' },
  { href: page('withdrawal'), label: L.shop.withdrawal, icon: 'refresh' },
]);

function accountRow() {
  return isSignedIn()
    ? { href: ACCOUNT_SIGNED.href, label: ACCOUNT_SIGNED.label, icon: 'user' }
    : { href: ACCOUNT.href, label: L.shop.account, icon: 'user' };
}

// Sekce „Jipos.cz" — důležité podstránky dle handoveru; cíle = existující URL
// shopu (stejná kurace jako patička a topbar).
const SITE_LINKS = withPages([
  { href: page('shipping'), label: L.site.shipping, icon: 'truck' },
  { href: page('store'), label: L.site.store, icon: 'store' },
  { href: page('service'), label: L.site.service, icon: 'cog' },
  { href: page('b2b'), label: L.site.b2b, icon: 'card' },
  { href: page('ratings'), label: L.site.ratings, icon: 'star' },
  // Časté dotazy stojí u Kontaktů: obojí je „kam se obrátit" (2026-09-15)
  { href: FAQ.href, label: FAQ.navLabel, icon: 'help' },
  { href: page('contact'), label: L.site.contact, icon: 'mail' },
]);

let nav = null;
let backdrop = null;
let closeBtn = null;
let lastTrigger = null;
let openGroup = null; // li úrovně 1 s otevřenou úrovní 2
let openSub = null; // li úrovně 2 s otevřenou úrovní 3

let searchIndex = [];
const panelSearch = new Map(); // panel ul → instance vyhledávače
// Vyhledávač a spodní sekce panelů 2. a 3. úrovně se staví až při prvním
// otevření panelu (review B4-06): v initu by to bylo ~54 kopií (~1 000 uzlů,
// ~200 listenerů) na každém pageview, i na desktopu, kde drawer nikdo neotevře.
const lazyPanels = new Map(); // panel ul → stavba při prvním zobrazení
let startPos = null; // { group, sub? } — otevření draweru v aktuální kategorii

// --------------------------------------------------------------- helpers

function li(className) {
  const node = document.createElement('li');
  if (className) node.className = className;
  node.setAttribute('role', 'none');
  return node;
}

function iconCircle(name, className) {
  const span = document.createElement('span');
  span.className = className || 'bs-drawer__icon';
  span.appendChild(icon(name));
  return span;
}

function rootIcon(root) {
  return CAT_ICONS[root.path] || 'tag';
}

function scrollTop(panel) {
  if (panel) panel.scrollTop = 0;
}

// Skloňování počtů dle prototypu (tvary a pravidlo žijí v locale).
const plurKategorie = L.categoryCount;
const plurVysledky = L.resultsCount;

// „retez" najde „Řetězové pily" — délku měnit nesmí jen lowercase, diakritika
// jde pryč NFD rozkladem.
function norm(s) {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// --------------------------------------------------------------- stav úrovní

// Každá změna úrovně zavře rozepsaná hledání — panel se ukazuje čerstvý.
function clearActiveSearches() {
  for (const inst of panelSearch.values()) {
    if (inst.panel.classList.contains('is-searching')) inst.clear(false);
  }
}

// Zavřený drawer a nikdy neotevřené panely nemá prohlížeč rozkládat
// (`content-visibility: hidden` v _drawer.scss, ops/sweeps/2026-10-13-lcp-drawer
// §A): drawer je 35–47 % objektů největšího layoutu stránky. Třída `bs-laid`
// pustí obsah do layoutu a zpět se nevrací — zavírací animace by jinak jela
// s prázdným panelem a další otevření by stálo rozklad znovu.
const LAID = 'bs-laid';

function ensurePanel(panel) {
  if (!panel) return;
  const build = lazyPanels.get(panel);
  if (build) {
    lazyPanels.delete(panel);
    build();
  }
  panel.classList.add(LAID);
}

// Kořen a panely aktuální kategorie: synchronně při otevření, jinak v nečinnosti
// po `load`, aby první tap na hamburger nezaplatil rozklad, který dřív platilo
// načtení stránky.
function layOutStart() {
  if (!nav) return;
  nav.classList.add(LAID);
  if (!startPos) return;
  ensurePanel(startPos.group.querySelector(':scope > ul.menu-level-2'));
  if (startPos.sub) ensurePanel(startPos.sub.querySelector(':scope > div > ul.menu-level-3'));
}

// drawer žije jen ≤ $bp-xl (_drawer.scss mq-max(xl)); desktop panely nestaví
const MQ_DRAWER = '(max-width: 1024px)';

function layOutWhenIdle() {
  if (!window.matchMedia || !window.matchMedia(MQ_DRAWER).matches) return;
  const run = function () {
    const idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1); };
    idle(layOutStart, { timeout: 3000 });
  };
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
}

function showRoot() {
  clearActiveSearches();
  if (openSub) openSub.classList.remove('is-open');
  if (openGroup) openGroup.classList.remove('is-open');
  openSub = null;
  openGroup = null;
  if (nav) nav.dataset.bsLevel = '0';
}

function showGroup(item) {
  clearActiveSearches();
  if (openSub) openSub.classList.remove('is-open');
  openSub = null;
  if (openGroup && openGroup !== item) openGroup.classList.remove('is-open');
  openGroup = item;
  ensurePanel(item.querySelector(':scope > ul.menu-level-2'));
  item.classList.add('is-open');
  scrollTop(item.querySelector(':scope > ul.menu-level-2'));
  nav.dataset.bsLevel = '1';
}

function showSub(item) {
  clearActiveSearches();
  if (openSub && openSub !== item) openSub.classList.remove('is-open');
  openSub = item;
  ensurePanel(item.querySelector(':scope > div > ul.menu-level-3'));
  item.classList.add('is-open');
  // openGroup drží rodičovské li — showSub může přijít i rovnou (start pozice,
  // výsledek hledání), takže se dohledává, ať zpět z úrovně 3 má kam
  const parent = item.closest('ul.menu-level-1 > li');
  if (parent && openGroup !== parent) {
    if (openGroup) openGroup.classList.remove('is-open');
    openGroup = parent;
    ensurePanel(parent.querySelector(':scope > ul.menu-level-2'));
    parent.classList.add('is-open');
  }
  // úroveň 3 je fixed uvnitř transformované úrovně 2 → posunutý scroll rodiče
  // by ji odsunul mimo panel; obě proto začínají nahoře
  scrollTop(item.closest('ul.menu-level-2'));
  scrollTop(item.querySelector(':scope > div > ul.menu-level-3'));
  nav.dataset.bsLevel = '2';
}

// Panel, který uživatel právě vidí (kvůli ESC a hledání).
function activePanel() {
  if (nav.dataset.bsLevel === '2' && openSub) {
    return openSub.querySelector(':scope > div > ul.menu-level-3');
  }
  if (nav.dataset.bsLevel === '1' && openGroup) {
    return openGroup.querySelector(':scope > ul.menu-level-2');
  }
  return nav.querySelector('ul.menu-level-1');
}

// --------------------------------------------------------------- otevření

export function openMobileNav(trigger) {
  if (!nav) return;
  lastTrigger = trigger || null;
  // před fokusem: prohlížeč si před ním přepočítá styl a rozloží odkrytý obsah
  layOutStart();
  // na stránce kategorie se drawer otevírá v ní, ne na hlavním menu
  if (startPos) {
    showGroup(startPos.group);
    if (startPos.sub) showSub(startPos.sub);
  } else {
    showRoot();
  }
  scrollTop(nav.querySelector('ul.menu-level-1'));
  document.body.classList.add('bs-drawer-open');
  if (closeBtn) closeBtn.focus();
}

export function closeMobileNav() {
  if (!document.body.classList.contains('bs-drawer-open')) return;
  document.body.classList.remove('bs-drawer-open');
  // nativní třída default JS — držíme ji v souladu, ať se stavy nerozjedou
  document.body.classList.remove('navigation-window-visible');
  if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
  lastTrigger = null;
}

// Kde uživatel právě je: nativní `li.active` (Shoptet značí přesnou kategorii
// v menu server-side), fallback shoda canonical URL (canonical drží čistou
// cestu i na stránkované/filtrované kategorii; funguje i pro značky).
function computeStart(roots) {
  const activeLi = nav.querySelector('ul.menu-level-1 li.active');
  const canonical = document.querySelector('link[rel="canonical"]');
  const here = canonical ? pathOf(canonical.getAttribute('href')) : '';
  const isCurrent = (node, href) => (activeLi
    ? node === activeLi
    : !!here && here !== '/' && pathOf(href) === here);
  for (const root of roots) {
    if (isCurrent(root.el, root.href)) return root.groups.length ? { group: root.el } : null;
    for (const group of root.groups) {
      if (isCurrent(group.el, group.href)) {
        return { group: root.el, sub: group.links.length ? group.el : null };
      }
      for (const leaf of group.links) {
        if (isCurrent(leaf.el.closest('li'), leaf.href)) return { group: root.el, sub: group.el };
      }
    }
  }
  return null;
}

// --------------------------------------------------------------- vyhledávání

// Plochý index přes všechny 3 úrovně; ikona = ikona ROOT kategorie výsledku,
// `open` jen tam, kde má výsledek podúroveň.
function buildSearchIndex(roots) {
  const index = [];
  for (const root of roots) {
    const name = rootIcon(root);
    index.push({
      label: root.label, norm: norm(root.label), icon: name, path: '', href: root.href,
      open: root.groups.length
        ? function () { showGroup(root.el); }
        : null,
    });
    for (const group of root.groups) {
      index.push({
        label: group.label, norm: norm(group.label), icon: name, path: root.label, href: group.href,
        open: group.links.length
          ? function () { showSub(group.el); }
          : null,
      });
      for (const leaf of group.links) {
        index.push({
          label: leaf.label, norm: norm(leaf.label), icon: name,
          path: root.label + ' · ' + group.label, href: leaf.href, open: null,
        });
      }
    }
  }
  return index;
}

// Řádek výsledku: výsledek s podúrovní ji klikem otevírá (celý řádek, stejné
// pravidlo jako řádky menu), koncová položka naviguje na svou URL.
function buildHit(item) {
  const row = el('div', 'bs-drawer__hit-row' + (item.open ? ' has-drill' : ''));
  const a = el('a', 'bs-drawer__hit', { href: item.href });
  a.appendChild(iconCircle(item.icon));
  const txt = el('span', 'bs-drawer__hit-txt');
  const label = el('span', 'bs-drawer__hit-label');
  label.textContent = item.label;
  txt.appendChild(label);
  if (item.path) {
    const path = el('span', 'bs-drawer__hit-path');
    path.textContent = item.path;
    txt.appendChild(path);
  }
  a.appendChild(txt);
  row.appendChild(a);
  if (item.open) {
    drillOnClick(a, item.open);
    row.appendChild(drillButton(item.open));
  }
  return row;
}

// Vyhledávač jednoho panelu — vlastní pole, výsledky i stav `is-searching`
// na panelu; index je sdílený (hledá se vždy celý strom).
function buildSearchWidget(panel) {
  const rowLi = li('bs-drawer__search-row');
  const box = el('div', 'bs-drawer__search');
  box.appendChild(icon('search'));
  const input = el('input', 'bs-drawer__search-input', {
    type: 'text',
    placeholder: SEARCH_PLACEHOLDER,
    autocomplete: 'off',
    autocorrect: 'off',
    autocapitalize: 'off',
    spellcheck: 'false',
    'aria-label': SEARCH_PLACEHOLDER,
  });
  box.appendChild(input);
  const clearBtn = el('button', 'bs-drawer__search-clear', { type: 'button', 'aria-label': SEARCH_CLEAR });
  clearBtn.hidden = true;
  clearBtn.appendChild(icon('close'));
  box.appendChild(clearBtn);
  rowLi.appendChild(box);

  const resultsLi = li('bs-drawer__results');
  const countBox = el('div', 'bs-drawer__eyebrow');
  resultsLi.appendChild(countBox);
  const hitsBox = el('div', 'bs-drawer__hits');
  resultsLi.appendChild(hitsBox);
  const emptyBox = el('div', 'bs-drawer__empty');
  emptyBox.textContent = SEARCH_EMPTY;
  emptyBox.hidden = true;
  resultsLi.appendChild(emptyBox);

  function render(raw) {
    const q = norm(raw.trim());
    const active = q.length > 0;
    panel.classList.toggle('is-searching', active);
    clearBtn.hidden = !active;
    if (!active) {
      hitsBox.textContent = '';
      return;
    }
    const hits = [];
    for (const item of searchIndex) {
      if (item.norm.indexOf(q) === -1) continue;
      hits.push(item);
      if (hits.length === SEARCH_MAX) break;
    }
    countBox.textContent = plurVysledky(hits.length);
    emptyBox.hidden = hits.length > 0;
    hitsBox.textContent = '';
    for (const item of hits) hitsBox.appendChild(buildHit(item));
  }

  function clear(focus) {
    input.value = '';
    render('');
    if (focus) input.focus();
  }

  input.addEventListener('input', function () {
    render(input.value);
  });
  clearBtn.addEventListener('click', function () {
    clear(true);
  });

  const inst = { rowLi, resultsLi, clear, panel };
  panelSearch.set(panel, inst);
  return inst;
}

// --------------------------------------------------------------- dekorace

// Hlava panelu: zavřít vlevo (u palce), logo vpravo.
function buildHead() {
  const head = document.createElement('div');
  head.className = 'bs-drawer__head';

  closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'bs-drawer__close';
  closeBtn.setAttribute('aria-label', LABEL_CLOSE);
  closeBtn.appendChild(icon('close'));
  closeBtn.addEventListener('click', closeMobileNav);
  head.appendChild(closeBtn);

  const logo = document.querySelector('.site-name img');
  if (logo) {
    const copy = logo.cloneNode(false);
    copy.className = 'bs-drawer__logo';
    copy.removeAttribute('width');
    copy.removeAttribute('height');
    head.appendChild(copy);
  }
  return head;
}

// Zpět-řádek podúrovně (samostatný bordered řádek dle prototypu). Klikatelný
// je CELÝ řádek, ne jen text s šipkou (zadání Tomáše 2026-08-31 večer).
function buildBackRow(label, onBack) {
  const row = li('bs-drawer__back-row');
  const back = el('button', 'bs-drawer__back', { type: 'button' });
  back.appendChild(icon('arrow-left'));
  back.appendChild(document.createTextNode(label));
  back.addEventListener('click', function (e) {
    e.stopPropagation(); // řádek má vlastní handler — dvojí volání nevadí, ale je zbytečné
    onBack();
  });
  row.appendChild(back);
  row.addEventListener('click', onBack);
  return row;
}

// Hlavička podúrovně: [ikona]? + titulek s počtem + „Zobrazit vše →".
// Klik KAMKOLIV do hlavičky (ikona, název, počet) NAVIGUJE na výpis kategorie
// — totéž co „Zobrazit vše" (upřesnění Tomáše 2026-08-31 po nasazení:
// „když kliknu na hlavní kategorii, chci se dostat do kategorie Zahrada
// stejně jako když kliknu na zobrazit vše"). Zpět jde přes zpět-řádek.
function buildPanelHead(opts) {
  const row = li('bs-drawer__panel-head');
  if (opts.icon) row.appendChild(iconCircle(opts.icon, 'bs-drawer__icon bs-drawer__panel-icon'));
  const txt = el('span', 'bs-drawer__panel-txt');
  const heading = el('span', 'bs-drawer__title');
  heading.textContent = opts.title;
  txt.appendChild(heading);
  const count = el('span', 'bs-drawer__count');
  count.textContent = plurKategorie(opts.count);
  txt.appendChild(count);
  row.appendChild(txt);
  if (opts.href) {
    const all = el('a', 'bs-drawer__all', { href: opts.href });
    all.appendChild(document.createTextNode(LABEL_ALL));
    all.insertAdjacentHTML('beforeend', iconHtml('arrow-right'));
    row.appendChild(all);
    row.classList.add('has-link');
    row.addEventListener('click', function (e) {
      if (e.target.closest('a')) return; // „Zobrazit vše" naviguje sám
      window.location.assign(opts.href);
    });
  }
  return row;
}

// Řádek s podúrovní: klik KAMKOLIV do řádku (název, obrázek, chevron) otevírá
// podúroveň — navigaci na výpis nese „Zobrazit vše" v hlavičce panelu.
// preventDefault ruší nativní href, stopPropagation delegovaný „collapsible"
// handler default JS (přepnul by nativní toggle a stavy by se rozjely).
function drillOnClick(elm, handler) {
  if (!elm) return;
  elm.addEventListener('click', function (e) {
    e.preventDefault();
    e.stopPropagation();
    handler();
  });
}

// Chevron tlačítko — viditelná šipka podúrovně (a druhý cíl pro tentýž drill).
function drillButton(handler) {
  const btn = el('button', 'bs-drawer__drill', { type: 'button', 'aria-label': LABEL_DRILL });
  btn.appendChild(icon('chevron-down', 'bs-drawer__chev'));
  btn.addEventListener('click', function (e) {
    e.stopPropagation();
    handler();
  });
  return btn;
}

function decorateLevel3(group, root) {
  if (!group.sub) return;
  // třída na nativních li — CSS řádků nesmí chytat vkládané sekce panelu
  for (const item of group.sub.querySelectorAll(':scope > li')) {
    item.classList.add('bs-drawer__row3');
  }
  const back = buildBackRow(root.label, function () {
    showGroup(root.el);
  });
  group.sub.prepend(back, buildPanelHead({ title: group.label, count: group.links.length, href: group.href }));
  lazyPanels.set(group.sub, function () {
    const search = buildSearchWidget(group.sub);
    back.after(search.rowLi, search.resultsLi);
    for (const row of buildExtras()) group.sub.appendChild(row);
  });
}

function decorateLevel2(root) {
  if (!root.sub) return;
  for (const group of root.groups) {
    group.el.classList.add('bs-drawer__row');
    // obrázek položky; bez `menu-image` (např. Značky) fallback glyph
    if (!group.image) {
      const thumb = el('span', 'bs-drawer__thumb');
      thumb.appendChild(icon('photo'));
      group.el.prepend(thumb);
    }
    // textový sloupec: nativní span s názvem + počet položek 3. úrovně pod ním
    const label = group.link.querySelector(':scope > span');
    if (label) {
      const txt = el('span', 'bs-drawer__txt');
      group.link.insertBefore(txt, label);
      txt.appendChild(label);
      if (group.links.length) {
        const count = el('span', 'bs-drawer__count');
        count.textContent = plurKategorie(group.links.length);
        txt.appendChild(count);
      }
    }
    if (group.links.length) {
      group.el.classList.add('has-drill');
      const drill = function () {
        showSub(group.el);
      };
      drillOnClick(group.link, drill);
      drillOnClick(group.image, drill);
      group.el.appendChild(drillButton(drill));
      decorateLevel3(group, root);
    }
  }
  const back = buildBackRow(LABEL_ROOT, showRoot);
  root.sub.prepend(
    back,
    buildPanelHead({
      icon: rootIcon(root),
      title: root.label,
      count: root.groups.length,
      href: root.href,
    })
  );
  lazyPanels.set(root.sub, function () {
    const search = buildSearchWidget(root.sub);
    back.after(search.rowLi, search.resultsLi);
    for (const row of buildExtras()) root.sub.appendChild(row);
  });
}

function decorateLevel1(roots) {
  for (const root of roots) {
    // třída na nativním li — CSS řádku kategorie nesmí chytat naše vložené li
    root.el.classList.add('bs-drawer__cat');
    root.link.prepend(iconCircle(rootIcon(root)));
    if (root.path === SALE_PATH) {
      root.el.classList.add('is-sale');
      // drawer kreslí nativní odkaz — název deštníku kampaně (readMenu) do něj
      const b = root.link.querySelector('b');
      if (b && b.textContent.trim() !== root.label) b.textContent = root.label;
    }
    if (root.path === OUTLET_PATH) root.el.classList.add('is-outlet');
    if (root.groups.length) {
      root.el.classList.add('has-drill');
      const drill = function () {
        showGroup(root.el);
      };
      drillOnClick(root.link, drill);
      root.el.appendChild(drillButton(drill));
      decorateLevel2(root);
    }
  }
}

// --------------------------------------------------------------- sekce a podpora

function eyebrowLi(text, modifier) {
  const row = li('bs-drawer__eyebrow' + (modifier ? ' ' + modifier : ''));
  row.textContent = text;
  return row;
}

function sepLi() {
  return li('bs-drawer__sep');
}

function sectionRow(item) {
  const row = li();
  const a = el('a', 'bs-drawer__service', { href: item.href, rel: 'nofollow' });
  a.appendChild(icon(item.icon));
  a.appendChild(document.createTextNode(item.label));
  row.appendChild(a);
  return row;
}

// Druhý shop firmy (přepínač CZ ↔ SK, countrySwitch.js; mock 2026-10-05):
// poslední řádek sekce shopu, vlajka na místě ikony. Cíl podle stránky dosadí
// sisterLink, na detailu produktu i adresu téhož produktu.
function sisterRow() {
  const row = li();
  const a = sisterLink(el('a', 'bs-drawer__service bs-drawer__service--sister', { rel: 'nofollow' }));
  a.appendChild(flag(SISTER.country));
  a.appendChild(document.createTextNode(SHOP.sisterShop.label));
  row.appendChild(a);
  return row;
}

// Tmavá karta podpory: foto + telefon, pod oddělovačem stav podpory a e-mail.
// Stav jde podle pracovní doby PODPORY (isSupportOnline, Po–Čt 8:00–15:00, Pá 7:30–14:30),
// ne prodejny — „Právě otevřeno" v sobotu slibovalo odpověď, která nepřijde.
function buildSupportCard() {
  const card = li('bs-drawer__support');

  const top = el('div', 'bs-drawer__support-top');
  const photo = el('span', 'bs-drawer__support-photo');
  fillAvatar(photo, 'drawer', 46);
  top.appendChild(photo);
  // hlavní kontakt karty: chat → e-mail → telefon (reachContact); e-mail pod
  // čarou zůstává vždy
  const reach = reachContact();
  const call = el('a', 'bs-drawer__support-call', { href: reach.href });
  if (reach.chat) asChatTrigger(call, 'drawer');
  const lead = el('span', 'bs-drawer__support-lead');
  lead.textContent = LABEL_SUPPORT;
  call.appendChild(lead);
  const num = el('span', 'bs-drawer__support-phone');
  num.textContent = reach.text;
  call.appendChild(num);
  top.appendChild(call);
  card.appendChild(top);

  const meta = el('div', 'bs-drawer__support-meta');
  const open = isSupportOnline();
  const dot = el('span', 'bs-drawer__support-dot' + (open ? ' is-open' : ''), { 'aria-hidden': 'true' });
  meta.appendChild(dot);
  const state = el('span', 'bs-drawer__support-state');
  state.textContent = (open ? HOURS.supportOnline : HOURS.supportOffline) + ' · ' + CONTACT.hours;
  meta.appendChild(state);
  const mail = el('a', 'bs-drawer__support-mail', { href: 'mailto:' + CONTACT.email });
  mail.textContent = CONTACT.email;
  meta.appendChild(mail);
  card.appendChild(meta);

  return card;
}

// Stav objednávky: jediný plný prvek draweru (protějšek tmavého pillu topbaru).
function orderStatusRow() {
  const href = page('orderStatus');
  if (href === null) return null;
  const row = li('bs-drawer__order');
  const a = el('a', 'bs-drawer__order-btn', { href, rel: 'nofollow' });
  a.appendChild(icon('package'));
  a.appendChild(document.createTextNode(L.orderStatus));
  a.appendChild(icon('arrow-right', 'bs-drawer__order-arrow'));
  row.appendChild(a);
  return row;
}

// Spodní sekce panelu — v KAŽDÉM panelu (rozhodnutí Tomáše), proto se staví
// vždy čerstvé kopie (jeden sdílený uzel nejde mít ve dvou panelech naráz).
function buildExtras() {
  const rows = [orderStatusRow(), sepLi(), eyebrowLi(L.eyebrowShopping)].filter(Boolean);
  for (const item of [accountRow()].concat(SHOP_LINKS)) {
    if (item.enabled === false) continue;
    rows.push(sectionRow(item));
  }
  rows.push(sepLi(), eyebrowLi(SHOP.shop.name));
  for (const item of SITE_LINKS) rows.push(sectionRow(item));
  rows.push(sisterRow());
  rows.push(buildSupportCard());
  return rows;
}

// --------------------------------------------------------------- init

export function initMobileNav() {
  nav = document.getElementById('navigation');
  if (!nav || nav.dataset.bsDrawer) return;
  const list = nav.querySelector('ul.menu-level-1');
  if (!list) return;
  const roots = readMenu();
  if (!roots.length) return;
  nav.dataset.bsDrawer = '1';
  nav.dataset.bsLevel = '0';
  nav.setAttribute('aria-label', LABEL_NAV);

  searchIndex = buildSearchIndex(roots);
  decorateLevel1(roots);
  startPos = computeStart(roots);

  const search = buildSearchWidget(list);
  list.prepend(search.rowLi, search.resultsLi, eyebrowLi(L.categoriesEyebrow, 'bs-drawer__eyebrow--cats'));
  for (const row of buildExtras()) list.appendChild(row);

  nav.prepend(buildHead());

  // backdrop patří do #headeru (tam žije #navigation) — sdílí s ním stacking
  // kontext, takže se panel i podklad řadí jednou z-index škálou
  const header = document.getElementById('header') || nav.parentNode;
  backdrop = header.querySelector(':scope > .bs-drawer-backdrop');
  if (!backdrop) {
    backdrop = document.createElement('div');
    backdrop.className = 'bs-drawer-backdrop';
    backdrop.addEventListener('click', closeMobileNav);
    header.appendChild(backdrop);
  }

  // hamburger: klik přebíráme v capture fázi (default JS by přepnul jen
  // nativní body třídu a náš stav by o tom nevěděl).
  //
  // ⚠️ TAP obsluhujeme UŽ na touchend (nález 2026-08-31, Tomášovy miss tapy):
  // nativní mobile-header JS dělá na touchend hamburgeru `preventDefault()`,
  // prohlížeč pak NESYNTETIZUJE click a click handler se nikdy nespustí —
  // tap na hamburger byl na dotykovém zařízení mrtvý pokaždé, když nativní
  // handler závod o event vyhrál (v Playwright emulaci reprodukovatelně:
  // touchend přišel s defaultPrevented=true a click nevznikl vůbec).
  // Tap = touchend bez posunu prstu (>12 px = scroll/drag, nechat být);
  // vlastní preventDefault + stopImmediatePropagation srazí nativní toggle
  // i případný syntetizovaný click, `lastTouchToggle` kryje dvojité spuštění
  // tam, kde click přece jen vznikne.
  const toggles = document.querySelectorAll(
    '.bs-hamburger, .navigation-buttons a.toggle-window[data-target="navigation"]'
  );
  let lastTouchToggle = 0;
  for (const toggle of toggles) {
    const toggleDrawer = function () {
      if (document.body.classList.contains('bs-drawer-open')) closeMobileNav();
      else openMobileNav(toggle);
    };
    let touchX = 0;
    let touchY = 0;
    toggle.addEventListener(
      'touchstart',
      function (e) {
        const t = e.changedTouches[0];
        touchX = t.clientX;
        touchY = t.clientY;
      },
      { passive: true, capture: true }
    );
    toggle.addEventListener(
      'touchend',
      function (e) {
        const t = e.changedTouches[0];
        if (Math.abs(t.clientX - touchX) > 12 || Math.abs(t.clientY - touchY) > 12) return;
        if (e.cancelable) e.preventDefault();
        e.stopImmediatePropagation();
        lastTouchToggle = Date.now();
        toggleDrawer();
      },
      true
    );
    toggle.addEventListener(
      'click',
      function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (Date.now() - lastTouchToggle < 700) return; // tap už obsloužil touchend
        toggleDrawer();
      },
      true
    );
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !document.body.classList.contains('bs-drawer-open')) return;
    const panel = activePanel();
    const inst = panel && panelSearch.get(panel);
    if (inst && panel.classList.contains('is-searching')) inst.clear(true);
    else if (nav.dataset.bsLevel === '2') showGroup(openGroup);
    else if (nav.dataset.bsLevel === '1') showRoot();
    else closeMobileNav();
  });

  document.documentElement.classList.add('bs-nav-ready');
  layOutWhenIdle();
}
